import type { Attachment } from '../../api/types'

/** What a video or audio attachment in a message can show (contract: "Attachments", media state). */
export type ClipView =
  /** Still in the outbox: nothing to play yet. */
  | { state: 'sending' }
  /** Being converted. Only its uploader may fetch it meanwhile, and plays it when this browser can. */
  | { state: 'processing'; playable: boolean }
  /** It couldn't be converted: its uploader can still download it; others can't fetch it. */
  | { state: 'failed'; download: boolean }
  | { state: 'ready' }

const support = new Map<string, boolean>()

export const mediaSupport = {
  /** Whether this browser may play a file of this type (canPlayType's "maybe" or "probably"). */
  canPlay: (kind: 'video' | 'audio', contentType: string): boolean => {
    const key = `${kind} ${contentType}`
    let playable = support.get(key)
    if (playable === undefined) {
      const probe = document.createElement(kind)
      playable = typeof probe.canPlayType === 'function' && probe.canPlayType(contentType) !== ''
      support.set(key, playable)
    }
    return playable
  },
}

export function clipView(attachment: Attachment, { pending, mine }: { pending: boolean; mine: boolean }): ClipView {
  if (pending) return { state: 'sending' }
  const kind = attachment.kind === 'video' ? 'video' : 'audio'
  if (attachment.media === 'processing') return { state: 'processing', playable: mine && mediaSupport.canPlay(kind, attachment.contentType) }
  if (attachment.media === 'failed') return { state: 'failed', download: mine }
  return { state: 'ready' }
}

/**
 * A key that changes when the worker replaces a clip with its converted copy, so a player made for the upload asks for
 * the new file instead of the one it may have loaded.
 */
export function clipVersion(attachment: Attachment): string {
  return `${attachment.id}:${attachment.media}:${attachment.contentType}:${attachment.sizeBytes}`
}
